import { motion } from "framer-motion";
import { Reveal, Overline } from "@/components/Reveal";
import { useQuery } from "@tanstack/react-query";
import { api, waLink } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const spanClass = (size) => {
  if (size === "tall") return "md:row-span-2";
  if (size === "wide") return "md:col-span-2";
  return "";
};

export default function Collection() {
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });

  return (
    <section
      id="collection"
      className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32"
      data-testid="collection"
    >
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Reveal>
            <Overline>The Collection</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-2xl font-serif text-4xl font-light leading-tight md:text-6xl">
              Signature creations,{" "}
              <span className="gold-gradient-text italic">beautifully</span>{" "}
              presented.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="max-w-xs text-sm text-[#A894C2]">
            Each design is personalised & customisable — sizes, flavours and
            branding tailored to your occasion.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid auto-rows-[280px] grid-cols-1 gap-5 md:grid-cols-3">
        {products.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
            className={`group relative overflow-hidden rounded-2xl border border-[#311B4D] ${spanClass(
              p.size
            )}`}
            data-testid={`product-card-${i}`}
          >
            <img
              src={p.image}
              alt={p.name}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F0616] via-[#0F0616]/30 to-transparent" />
            <div className="absolute inset-0 border border-transparent transition-colors duration-500 group-hover:border-[#D4AF37]/60" />

            <div className="relative flex h-full flex-col justify-end p-7">
              <span className="text-[10px] uppercase tracking-[0.28em] text-gold">
                {p.category}
              </span>
              <h3 className="mt-2 font-serif text-2xl text-[#FDFBF7] md:text-3xl">
                {p.name}
              </h3>
              <p className="mt-1 text-sm italic text-[#A894C2]">{p.tagline}</p>

              <div className="mt-4 flex items-center justify-between">
                <span className="text-sm text-[#FDFBF7]">
                  From <span className="text-gold">&#8377;{p.price_from}</span>
                </span>
                <a
                  href={waLink(`Hi Elixz! I'm interested in the ${p.name}.`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`product-enquire-${i}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-[#0F0616] opacity-0 transition-all duration-500 group-hover:opacity-100"
                  aria-label={`Enquire about ${p.name}`}
                >
                  <ArrowUpRight size={18} />
                </a>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
